/**
 * UserModuleGrant — explicit override доступа к модулю per (user, entity, module).
 * access-control (2026-06-22). Конструктор прав поверх базовой роли:
 *   - отсутствие строки = дефолт из роли (ROLE_DEFAULT_MODULES, см. engines-pure/module-access)
 *   - строка allowed=true  = explicit allow (открыть модуль роли, у которой его нет)
 *   - строка allowed=false = explicit deny (закрыть модуль роли, у которой он есть)
 * Scoped по entity — гранты считаются для активной компании.
 */
import { z } from 'zod';
import { Module } from '../enums.js';
import { IsoDateTime, UuidString } from '../primitives.js';

export const UserModuleGrant = z.object({
  user_id: UuidString,
  entity_id: UuidString,
  module: Module,
  allowed: z.boolean(),
  updated_by: UuidString.nullable(),
  updated_at: IsoDateTime,
});
export type UserModuleGrant = z.infer<typeof UserModuleGrant>;

/** Одна строка matrix-ответа admin-API: модуль + источник (роль-дефолт / explicit) + итог. */
export const ModuleGrantRow = z.object({
  module: Module,
  /** Дефолт из роли (ROLE_DEFAULT_MODULES). */
  default_allowed: z.boolean(),
  /** Явный override (null = нет строки → действует default). */
  override: z.boolean().nullable(),
  /** Итоговый эффективный доступ = override ?? default_allowed. */
  effective: z.boolean(),
});
export type ModuleGrantRow = z.infer<typeof ModuleGrantRow>;

/** GET /admin/users/:id/module-grants?entity_id= — matrix для рендера. */
export const ModuleGrantMatrix = z.object({
  user_id: UuidString,
  entity_id: UuidString,
  role: z.string(),
  rows: z.array(ModuleGrantRow),
});
export type ModuleGrantMatrix = z.infer<typeof ModuleGrantMatrix>;

/** PUT body — набор изменений (upsert allow/deny; null = reset-to-default = удалить override). */
export const ModuleGrantUpdate = z
  .object({
    grants: z
      .array(
        z
          .object({
            module: Module,
            allowed: z.boolean().nullable(), // null = reset к роли-дефолту
          })
          // zod-strict-ok: input request-body
          .strict(),
      )
      .min(1)
      .max(8), // 8 модулей — верхняя граница набора в одном PUT
    reason: z.string().min(1).max(500),
  })
  // zod-strict-ok: input request-body
  .strict();
export type ModuleGrantUpdate = z.infer<typeof ModuleGrantUpdate>;
